/**
 * dataStore — 全局数据 Store（Zustand）
 *
 * 单次加载所有数据 + 预计算索引，4 个页面共享。
 * 消除原来各页面独立 fetch 的 20 次重复请求。
 */
import { create } from 'zustand';
import type { ChainNode, ChainEdge, Company, FinancialRecord, Ranking } from '../types';
import {
  loadNodes, loadEdges, loadCompanies, loadFinancials, loadRankings, loadIndex,
  type ChainIndex,
} from '../data/loader';

interface DataState {
  // 原始数据
  nodes: ChainNode[];
  edges: ChainEdge[];
  companies: Company[];
  financials: FinancialRecord[];
  rankings: Ranking[];
  index: ChainIndex | null;

  // 派生索引（运行时构建，O(1) 查询）
  nodeIndex: Map<string, ChainNode>;
  companyIndex: Map<string, Company>;
  rankingIndex: Map<string, Ranking>;

  // 状态
  loaded: boolean;
  loading: boolean;
  error: string | null;

  // 动作
  loadAll: () => Promise<void>;
}

export const useDataStore = create<DataState>((set, get) => ({
  nodes: [],
  edges: [],
  companies: [],
  financials: [],
  rankings: [],
  index: null,

  nodeIndex: new Map(),
  companyIndex: new Map(),
  rankingIndex: new Map(),

  loaded: false,
  loading: false,
  error: null,

  loadAll: async () => {
    if (get().loaded || get().loading) return;
    set({ loading: true, error: null });
    try {
      const [nodes, edges, companies, financials, rankings, index] = await Promise.all([
        loadNodes(),
        loadEdges(),
        loadCompanies(),
        loadFinancials(),
        loadRankings(),
        loadIndex(),
      ]);

      // 构建派生索引
      const nodeIndex = new Map<string, ChainNode>();
      nodes.forEach(n => nodeIndex.set(n.id, n));

      const companyIndex = new Map<string, Company>();
      companies.forEach(c => companyIndex.set(c.company_id, c));

      const rankingIndex = new Map<string, Ranking>();
      rankings.forEach(r => rankingIndex.set(r.company_id, r));

      set({
        nodes, edges, companies, financials, rankings, index,
        nodeIndex, companyIndex, rankingIndex,
        loaded: true, loading: false, error: null,
      });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : '数据加载失败' });
      console.error('dataStore.loadAll failed:', err);
    }
  },
}));
